import { useLogger } from '@cotera/client/v0/actions/framework/logger';
import { LoginRequiredError, useAuth } from '@workos-inc/authkit-react';
import { useCallback, useRef } from 'react';
import type { WorkOsRedirectState } from '../pages/workos';
import { getBearerTokenWithCache } from './bearer-token-cache';

/**
 * Returns a stable getBearerToken function (same reference across re-renders).
 * WorkOS AuthKit React only exposes tokens via useAuth(); this hook wraps that
 * and uses a ref so the returned function identity doesn't change when auth
 * context updates, which avoids unnecessary re-subscriptions (e.g. Electric
 * shapes) when only the token getter reference would otherwise change.
 */
export const useGetBearerToken = (): ((options?: {
  forceRefresh?: boolean;
}) => Promise<string | undefined>) => {
  const { getAccessToken, user, signIn } = useAuth();
  const logger = useLogger();
  const authRef = useRef({ getAccessToken, user, logger, signIn });
  authRef.current = { getAccessToken, user, logger, signIn };

  const getBearerToken = useCallback(
    async (options?: { forceRefresh?: boolean }) => {
      const {
        getAccessToken: getToken,
        user: u,
        logger: log,
        signIn: doSignIn,
      } = authRef.current;

      return getBearerTokenWithCache({
        userId: u?.id ?? null,
        forceRefresh: options?.forceRefresh,
        fetchBearerToken: async () => {
          if (u === null) {
            return undefined;
          }
          try {
            // forceRefresh makes WorkOS mint a new session token even when the
            // current one hasn't expired by its own clock (used on 401 retry).
            const token = await getToken({
              forceRefresh: options?.forceRefresh,
            });
            return `Bearer ${token}`;
          } catch (error) {
            // The React user state is only updated on successful refresh, so it can
            // still be non-null even after the underlying session/refresh token has
            // expired. Redirect to sign-in rather than surfacing an unhandled error.
            if (error instanceof LoginRequiredError) {
              const state: WorkOsRedirectState = {
                t: 'sign-in',
                returnTo: window.location.pathname + window.location.search,
              };
              void doSignIn({ state });
              return undefined;
            }
            log.error('Failed to get bearer token', error, {
              payload: {
                userId: u?.id,
              },
            });
            throw error;
          }
        },
      });
    },
    []
  );

  return getBearerToken;
};
